import React, { useRef, useState } from "react";
import { Table, Input, Button, Space } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import type { InputRef, TableColumnsType, TableColumnType } from "antd";
import useSWR from 'swr';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const fetcher = (url: string) => fetch(url).then((res) => {
    if (!res.ok) {
        throw new Error('Failed to fetch data');
    }
    return res.json();
});

const formatDateTime = (dateStr: string) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const pad = (num: number) => String(num).padStart(2, '0');
    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1);
    const year = d.getFullYear();
    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());
    const seconds = pad(d.getSeconds());
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

interface DataType {
    key: string;
    dateTransaction: string;
    noDocument: string;
    itemCode: string;
    qty: number;
    codeTrans: string;
    warehouse: string;
}

type DataIndex = keyof DataType;

export const HocLogSparepartsTable: React.FC = () => {
    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);
    const { data, isLoading } = useSWR(`${backendUrl}/api/log-spareparts`, fetcher);

    const handleSearch = (
        selectedKeys: string[],
        confirm: () => void,
        dataIndex: DataIndex
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${String(dataIndex)}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: "Date Transaction",
            dataIndex: "dateTransaction",
            key: "dateTransaction",
            sorter: (a, b) => a.dateTransaction.localeCompare(b.dateTransaction),
            ...getColumnSearchProps("dateTransaction"),
        },
        {
            title: "No Document",
            dataIndex: "noDocument",
            key: "noDocument",
            sorter: (a, b) => a.noDocument.localeCompare(b.noDocument),
            ...getColumnSearchProps("noDocument"),
        },
        {
            title: "Item Code",
            dataIndex: "itemCode",
            key: "itemCode",
            sorter: (a, b) => a.itemCode.localeCompare(b.itemCode),
            ...getColumnSearchProps("itemCode"),
        },
        {
            title: "Qty",
            dataIndex: "qty",
            key: "qty",
            sorter: (a, b) => a.qty - b.qty,
            ...getColumnSearchProps("qty"),
        },
        {
            title: "Code Trans",
            dataIndex: "codeTrans",
            key: "codeTrans",
            sorter: (a, b) => a.codeTrans.localeCompare(b.codeTrans),
            ...getColumnSearchProps("codeTrans"),
            render: (value: string) => (value === "100" ? "100 (IN)" : value === "200" ? "200 (OUT)" : value),
        },
        {
            title: "Warehouse",
            dataIndex: "warehouse",
            key: "warehouse",
            sorter: (a, b) => a.warehouse.localeCompare(b.warehouse),
            ...getColumnSearchProps("warehouse"),
        },
    ];

    const dataSource: DataType[] = data ? data.map((item: any) => ({
        key: String(item.id),
        dateTransaction: formatDateTime(item.date_trans),
        noDocument: item.no_document || "",
        itemCode: item.item_code || "",
        qty: parseFloat(item.qty || "0"),
        codeTrans: item.code_trans || "",
        warehouse: item.warehouse || "",
    })) : [];

    return <Table columns={columns} dataSource={dataSource} loading={isLoading} />;
};